/**
 * A catalog entry in the record panel. The id is `<catalogKind>/<name>`,
 * the same pair the plugin page's route takes. Install and configure stay
 * on the page and the card; this is the read.
 */
import { useQuery } from '@tanstack/react-query';

import { getCatalogEntry } from '@optum/api-client/endpoints/catalog';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDate } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { IconTile, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { parsePluginId } from './plugin-id.js';

export function PluginRecord({ record }: { record: RecordRef }) {
  const parsed = parsePluginId(record.id);
  const kind = parsed?.kind ?? 'capabilities';
  const name = parsed?.name ?? '';
  const entry = useQuery({
    queryKey: qk.catalogEntry(kind, name),
    queryFn: () => getCatalogEntry(kind, name),
    enabled: parsed !== null,
  });
  if (!parsed) return <RecordMissing record={record} />;

  return (
    <RecordAsync query={entry} record={record}>
      {(e) => {
        const latest = e.versions.find((v) => v.version === e.latest_version);
        return (
          <RecordColumn>
            <RecordIdentity
              avatar={
                <IconTile
                  icon="extension"
                  size={40}
                  tint="var(--violet-tint)"
                  color="var(--violet)"
                />
              }
              eyebrow={
                <>
                  <Typography variant="eyebrow">Plugin</Typography>
                  <StatusBadge status={e.kind} vocabulary="wheelKind" />
                  {e.latest_job ? (
                    <StatusBadge status={e.latest_job.state} vocabulary="installJob" />
                  ) : null}
                </>
              }
              name={e.name}
              subtitle={e.summary ?? undefined}
            />
            <RecordSection label="Versions">
              <RecordFacts
                items={[
                  { label: 'Latest', value: e.latest_version },
                  { label: 'Installed', value: e.installed_version ?? 'not installed' },
                  {
                    label: 'Upgrade',
                    value: e.upgrade_available ? 'available' : 'none',
                    mono: false,
                  },
                  {
                    label: 'Published',
                    value: latest ? absoluteDate(latest.published_at) : '—',
                  },
                  { label: 'Releases', value: String(e.versions.length) },
                ]}
              />
            </RecordSection>
            <RecordActions>
              <RecordOpen record={record} />
            </RecordActions>
          </RecordColumn>
        );
      }}
    </RecordAsync>
  );
}
